import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import ScrollToTop from './components/ScrollToTop';
import HomePage from './pages/HomePage';
import QuotePageV2 from './pages/QuotePageV2';
import ExtrasPage from './pages/ExtrasPage';
import DetailsPage from './pages/DetailsPage';
import PaymentPage from './pages/PaymentPage';
import ConfirmationPage from './pages/ConfirmationPage';
import CheckoutPage from './pages/CheckoutPage';
import CmsPage from './pages/CmsPage';
import FeedbackPage from './pages/FeedbackPage';
import AboutPage from './pages/AboutPage';
import FaqPage from './pages/FaqPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ClarityRouteTracker from './components/ClarityRouteTracker';
// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminBookingsPage from './pages/admin/AdminBookingsPage';
import AdminCmsPage from './pages/admin/AdminCmsPage';
import AdminPageEditorPage from './pages/admin/AdminPageEditorPage';
import AdminCustomersPage from './pages/admin/AdminCustomersPage';
import AdminCustomerDetailPage from './pages/admin/AdminCustomerDetailPage';
import AdminRoutesPage from './pages/admin/AdminRoutesPage';
import AdminRoutesListPage from './pages/admin/AdminRoutesListPage';
import AdminPricingPage from './pages/admin/AdminPricingPage';
import AdminAirportsPage from './pages/admin/AdminAirportsPage';
import AdminPaymentMethodsPage from './pages/admin/AdminPaymentMethodsPage';
import AdminIntegrationsPage from './pages/admin/AdminIntegrationsPage';
import AdminEmailSettingsPage from './pages/admin/AdminEmailSettingsPage';
import AdminCronMonitorPage from './pages/admin/AdminCronMonitorPage';
import AdminEmailLogsPage from './pages/admin/AdminEmailLogsPage';
import AdminFeedbackPage from './pages/admin/AdminFeedbackPage';
import AdminCouponsPage from './pages/admin/AdminCouponsPage';
import AdminTrackingPage from './pages/admin/AdminTrackingPage';
import AdminFxRatePage from './pages/admin/AdminFxRatePage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminFraudDashboard from './pages/admin/AdminFraudDashboard';
import AdminAbandonedCheckoutsPage from './pages/admin/AdminAbandonedCheckoutsPage';
import ManageBookingPage from './pages/ManageBookingPage';
import ContactPage from './pages/ContactPage';
import TrackingScriptLoader from './components/TrackingScriptLoader';
import WhatsAppButton from './components/WhatsAppButton';
import HomePageRu from './pages/HomePageRu';
import TrailingSlashEnforcer from './components/TrailingSlashEnforcer';
// SECURITY: Protected Route for admin authentication
import ProtectedRoute from './components/auth/ProtectedRoute';
// SEO tags are pre-rendered at build time by generate-static-seo.js

function App() {
  return (
    <HelmetProvider>
    <Router>
      {/* Trailing slash enforcer - redirects /path to /path/ for SEO dedup */}
      <TrailingSlashEnforcer />
      {/* Global SEO from seoConfig.js - must be inside Router for useLocation */}
      
      {/* Global scroll to top on route change */}
      <ScrollToTop />
      {/* Clarity SPA Route Tracker - ensures proper session recording */}
      <ClarityRouteTracker />
      <TrackingScriptLoader />
      <Toaster position="top-center" />
      <WhatsAppButton />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/ru/" element={<HomePageRu />} />
        <Route path="/ru" element={<HomePageRu />} />
        <Route path="/quote" element={<QuotePageV2 />} />
        <Route path="/quote/" element={<QuotePageV2 />} />
        <Route path="/extras" element={<ExtrasPage />} />
        <Route path="/extras/" element={<ExtrasPage />} />
        <Route path="/details" element={<DetailsPage />} />
        <Route path="/details/" element={<DetailsPage />} />
        <Route path="/payment" element={<PaymentPage />} />
        <Route path="/payment/" element={<PaymentPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/contact/" element={<ContactPage />} />
        <Route path="/confirm" element={<ConfirmationPage />} />
        <Route path="/confirm/" element={<ConfirmationPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/checkout/" element={<CheckoutPage />} />
        
        {/* Admin Routes - Hidden login URL for security */}
        <Route path="/login-goturkeytransfer" element={<AdminLoginPage />} />
        
        {/* SECURITY: All admin routes wrapped with ProtectedRoute for server-side auth verification */}
        <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/bookings" element={<ProtectedRoute><AdminBookingsPage /></ProtectedRoute>} />
        <Route path="/admin/bookings/:ref" element={<ProtectedRoute><AdminBookingsPage /></ProtectedRoute>} />
        <Route path="/admin/abandoned" element={<ProtectedRoute><AdminAbandonedCheckoutsPage /></ProtectedRoute>} />
        <Route path="/admin/customers" element={<ProtectedRoute><AdminCustomersPage /></ProtectedRoute>} />
        <Route path="/admin/customers/:id" element={<ProtectedRoute><AdminCustomerDetailPage /></ProtectedRoute>} />
        <Route path="/admin/routes" element={<ProtectedRoute><AdminRoutesPage /></ProtectedRoute>} />
        <Route path="/admin/routes-list" element={<ProtectedRoute><AdminRoutesListPage /></ProtectedRoute>} />
        <Route path="/admin/pricing" element={<ProtectedRoute><AdminPricingPage /></ProtectedRoute>} />
        <Route path="/admin/airports" element={<ProtectedRoute><AdminAirportsPage /></ProtectedRoute>} />
        <Route path="/admin/payment-methods" element={<ProtectedRoute><AdminPaymentMethodsPage /></ProtectedRoute>} />
        <Route path="/admin/settings/integrations" element={<ProtectedRoute><AdminIntegrationsPage /></ProtectedRoute>} />
        <Route path="/admin/settings/email" element={<ProtectedRoute><AdminEmailSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/settings/cron" element={<ProtectedRoute><AdminCronMonitorPage /></ProtectedRoute>} />
        <Route path="/admin/settings/email-logs" element={<ProtectedRoute><AdminEmailLogsPage /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedbackPage /></ProtectedRoute>} />
        <Route path="/admin/coupons" element={<ProtectedRoute><AdminCouponsPage /></ProtectedRoute>} />
        <Route path="/admin/tracking" element={<ProtectedRoute><AdminTrackingPage /></ProtectedRoute>} />
        <Route path="/admin/fx-rate" element={<ProtectedRoute><AdminFxRatePage /></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute><AdminReportsPage /></ProtectedRoute>} />
        <Route path="/admin/fraud" element={<ProtectedRoute><AdminFraudDashboard /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><AdminSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/pages" element={<ProtectedRoute><AdminCmsPage /></ProtectedRoute>} />
        <Route path="/admin/pages/new" element={<ProtectedRoute><AdminPageEditorPage /></ProtectedRoute>} />
        <Route path="/admin/pages/edit/:id" element={<ProtectedRoute><AdminPageEditorPage /></ProtectedRoute>} />
        {/* Redirect old admin URLs for security */}
        <Route path="/admin/login" element={<Navigate to="/" replace />} />
        <Route path="/admin" element={<Navigate to="/" replace />} />
        
        {/* CMS Dynamic Pages - Exact paths first */}
        <Route path="/istanbul-airport-transfer" element={<CmsPage />} />
        <Route path="/istanbul-airport-transfer/" element={<CmsPage />} />
        <Route path="/antalya-airport-transfer" element={<CmsPage />} />
        <Route path="/antalya-airport-transfer/" element={<CmsPage />} />
        <Route path="/dalaman-airport-transfer" element={<CmsPage />} />
        <Route path="/dalaman-airport-transfer/" element={<CmsPage />} />
        <Route path="/bodrum-airport-transfer" element={<CmsPage />} />
        <Route path="/bodrum-airport-transfer/" element={<CmsPage />} />
        <Route path="/turkey-vip-transfer-services" element={<CmsPage />} />
        <Route path="/turkey-vip-transfer-services/" element={<CmsPage />} />
        <Route path="/medical-tourism-turkey-vip-transfers" element={<CmsPage />} />
        <Route path="/medical-tourism-turkey-vip-transfers/" element={<CmsPage />} />
        <Route path="/antalya-airport-to-side-transfer" element={<CmsPage />} />
        <Route path="/antalya-airport-to-side-transfer/" element={<CmsPage />} />
        <Route path="/antalya-airport-to-belek-transfer" element={<CmsPage />} />
        <Route path="/antalya-airport-to-belek-transfer/" element={<CmsPage />} />
        <Route path="/antalya-airport-to-kemer-transfer" element={<CmsPage />} />
        <Route path="/antalya-airport-to-kemer-transfer/" element={<CmsPage />} />
        <Route path="/antalya-airport-to-alanya-transfer" element={<CmsPage />} />
        <Route path="/antalya-airport-to-alanya-transfer/" element={<CmsPage />} />
        <Route path="/dalaman-airport-to-fethiye-transfer" element={<CmsPage />} />
        <Route path="/dalaman-airport-to-fethiye-transfer/" element={<CmsPage />} />
        <Route path="/dalaman-airport-to-oludeniz-transfer" element={<CmsPage />} />
        <Route path="/dalaman-airport-to-oludeniz-transfer/" element={<CmsPage />} />
        <Route path="/dalaman-airport-to-marmaris-transfer" element={<CmsPage />} />
        <Route path="/dalaman-airport-to-marmaris-transfer/" element={<CmsPage />} />
        <Route path="/bodrum-airport-to-turgutreis-transfer" element={<CmsPage />} />
        <Route path="/bodrum-airport-to-turgutreis-transfer/" element={<CmsPage />} />
        <Route path="/bodrum-airport-to-yalikavak-transfer" element={<CmsPage />} />
        <Route path="/bodrum-airport-to-yalikavak-transfer/" element={<CmsPage />} />
        <Route path="/antalya-airport-transfer-guide" element={<CmsPage />} />
        <Route path="/antalya-airport-transfer-guide/" element={<CmsPage />} />
        <Route path="/corporate-travel" element={<CmsPage />} />
        <Route path="/corporate-travel/" element={<CmsPage />} />
        
        {/* CMS pages from admin - additional routes */}
        <Route path="/sabiha-gokcen-to-taksim-transfer" element={<CmsPage />} />
        <Route path="/sabiha-gokcen-to-taksim-transfer/" element={<CmsPage />} />
        <Route path="/istanbul-airport-to-sultanahmet-transfer" element={<CmsPage />} />
        <Route path="/istanbul-airport-to-sultanahmet-transfer/" element={<CmsPage />} />
        <Route path="/istanbul-airport-to-taksim-transfer" element={<CmsPage />} />
        <Route path="/istanbul-airport-to-taksim-transfer/" element={<CmsPage />} />
        <Route path="/bodrum-airport-to-bodrum-center-transfer" element={<CmsPage />} />
        <Route path="/bodrum-airport-to-bodrum-center-transfer/" element={<CmsPage />} />
        <Route path="/istanbul-airport-transfer-best-transport" element={<CmsPage />} />
        <Route path="/istanbul-airport-transfer-best-transport/" element={<CmsPage />} />
        <Route path="/turkey-airport-transfer-service-guide" element={<CmsPage />} />
        <Route path="/turkey-airport-transfer-service-guide/" element={<CmsPage />} />
        <Route path="/airport-transfer-istanbul" element={<CmsPage />} />
        <Route path="/airport-transfer-istanbul/" element={<CmsPage />} />
        
        <Route path="/cancellation-refund-policy" element={<CmsPage />} />
        <Route path="/cancellation-refund-policy/" element={<CmsPage />} />
        <Route path="/turkey-vip-transfer-faq" element={<CmsPage />} />
        <Route path="/turkey-vip-transfer-faq/" element={<CmsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/about/" element={<AboutPage />} />
        <Route path="/about-us" element={<AboutPage />} />
        <Route path="/about-us/" element={<AboutPage />} />
        <Route path="/terms-of-service" element={<CmsPage />} />
        <Route path="/terms-of-service/" element={<CmsPage />} />
        <Route path="/terms-and-conditions" element={<CmsPage />} />
        <Route path="/terms-and-conditions/" element={<CmsPage />} />
        <Route path="/privacy-policy" element={<CmsPage />} />
        <Route path="/privacy-policy/" element={<CmsPage />} />
        <Route path="/cookie-policy" element={<CmsPage />} />
        <Route path="/cookie-policy/" element={<CmsPage />} />
        
        {/* FAQ Page */}
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/faq/" element={<FaqPage />} />
        
        {/* Blog Page */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogDetailPage />} />
        <Route path="/blog/:slug/" element={<BlogDetailPage />} />
        
        {/* Legacy CMS paths */}
        <Route path="/contact-us" element={<CmsPage />} />
        <Route path="/contact-us/" element={<CmsPage />} />
        <Route path="/privacy" element={<CmsPage />} />
        <Route path="/privacy/" element={<CmsPage />} />
        <Route path="/terms" element={<CmsPage />} />
        <Route path="/terms/" element={<CmsPage />} />
        <Route path="/refund" element={<CmsPage />} />
        <Route path="/refund/" element={<CmsPage />} />
        
        {/* Dynamic token routes (no trailing slash enforcement) */}
        <Route path="/manage/:token" element={<ManageBookingPage />} />
        <Route path="/manage/:token/" element={<ManageBookingPage />} />
        <Route path="/feedback/:token" element={<FeedbackPage />} />
        <Route path="/feedback/:token/" element={<FeedbackPage />} />
        <Route path="/:slug" element={<CmsPage />} />
        <Route path="/:slug/" element={<CmsPage />} />
      </Routes>
    </Router>
    </HelmetProvider>
  );
}

export default App;
